import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, photoUrl, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { BannerMockup } from "../components/BannerMockup";
import {
  Megaphone, ArrowRight, Eye, TrendingUp, Users, ShieldCheck, Check, MapPin, Sparkles,
  Monitor, LayoutList, Square, PanelLeft, BadgeCheck, ImagePlus, CircleDot, Share2, Loader2, LayoutGrid, Plus, Layers, Star, Pin,
} from "lucide-react";

const PLACEMENT_META = [
  { key: "home_top", icon: Monitor, img: "https://static.prod-images.emergentagent.com/jobs/0bb35e6b-e46a-4892-929f-72cf88d0a5d8/images/649e6b64c8feac4e708b34db696006ed536f9c0f960e290881c5e221b4e8668f.jpeg", size: "1200 × 120 px", premium: true },
  { key: "home", icon: Monitor, img: "https://static.prod-images.emergentagent.com/jobs/0bb35e6b-e46a-4892-929f-72cf88d0a5d8/images/19cc6648f56750b7daa8d2b0c13a3fdf7b9c11343ecd144efdd9977e1cbc0c28.jpeg", size: "1200 × 120 px" },
  { key: "browse", icon: LayoutList, img: "https://static.prod-images.emergentagent.com/jobs/0bb35e6b-e46a-4892-929f-72cf88d0a5d8/images/29008d1464e291b8f8ecc87fdd470b7f19270fa9b5b94539cd35b0fa37179abb.jpeg", size: "1200 × 120 px" },
  { key: "browse_bottom", icon: LayoutList, img: "https://static.prod-images.emergentagent.com/jobs/0bb35e6b-e46a-4892-929f-72cf88d0a5d8/images/4125781dfdb5cf3b6e4ec9325e6c604c3974bbd50f203edb4b3f37a399b28062.jpeg", size: "1200 × 180 px" },
  { key: "browse_sidebar", icon: PanelLeft, img: "https://static.prod-images.emergentagent.com/jobs/0bb35e6b-e46a-4892-929f-72cf88d0a5d8/images/e87fe0b30bb58746b90125c6a3720fbdc2173a29502ea994d200b78e34a51787.jpeg", size: "600 × 500 px" },
  { key: "listing", icon: Square, img: "https://static.prod-images.emergentagent.com/jobs/0bb35e6b-e46a-4892-929f-72cf88d0a5d8/images/17fc8156247003f3524435480d91fc7ec679070dd833544743939467b21fce38.jpeg", size: "600 × 500 px" },
];

const BENEFIT_ICONS = [Users, Eye, TrendingUp, ShieldCheck];

const SAMPLE_BANNERS = [
  "https://static.prod-images.emergentagent.com/jobs/0bb35e6b-e46a-4892-929f-72cf88d0a5d8/images/4c8049cfc042f44be1cba2865382e1e1e1fb47a89f2adee169d1faf5b8f0bbe3.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/0bb35e6b-e46a-4892-929f-72cf88d0a5d8/images/49a4e77fb60ec501ba57972e5d4c4799e56ae21fdab7a4d2acc80e5bfd62a6ab.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/0bb35e6b-e46a-4892-929f-72cf88d0a5d8/images/5acee879545f43b7e95ee022982a1c25289e4a51f7a09018e60cc9dbe2f4271a.jpeg",
];

const COPY = {
  en: {
    hero_badge: "Where your ad appears",
    hero_title_1: "See exactly where your",
    hero_title_2: "banner will show",
    hero_sub: "CritterPosts.ca has six premium ad placements across its busiest pages. Here's a visual tour of each one — and why putting your brand here puts it in front of Canada's pet lovers.",
    cta_start: "Start advertising",
    cta_tour: "Tour the placements",
    from_price: (c, r) => <>From <strong className="text-white">{c} ${r}/day</strong> · automatic discounts on longer runs · pay by Interac e-Transfer</>,
    stats: [
      { value: "6", label: "premium placements" },
      { value: "100%", label: "share-of-voice options" },
      { value: "CAD", label: "flat daily pricing" },
      { value: "e-Transfer", label: "simple payment" },
    ],
    tour_title: "A visual tour of every slot",
    tour_sub: "Each highlighted zone below shows the exact spot your banner occupies on the live site.",
    try_hint: "Want to picture it? Toggle any slot to \"Preview my ad\" and drop in your own banner.",
    where_label: "Where it appears",
    preview_label: "Preview my ad",
    where_it_shows: "Where it shows:",
    best_for: "Best for:",
    per_day: "/day",
    ideal_image: "Ideal image:",
    book_slot: "Book this slot",
    book_banner: "Book this banner",
    compare_all: "Compare your banner across every slot",
    gallery_title: "Your banner across every slot",
    gallery_sub: "See how your creative looks in every placement at once, then book the one you want — or bundle several and save.",
    gallery_select: "Add to bundle",
    gallery_selected: "In bundle",
    book_n: (n) => `Book ${n} slots`,
    bundle_save: (pct) => `${pct}% bundle discount`,
    bundle_per_day: (cur, amt) => `${cur} $${amt}/day`,
    upload_cta: "Drag & drop your banner, or click to upload",
    drop_here: "Drop your banner to preview it",
    uploading: "Uploading…",
    uploaded_note: "That's your banner in this slot. Upload a different image anytime.",
    share_btn: "Share this preview",
    sharing: "Preparing link…",
    share_copied: "Preview link copied — send it to a colleague!",
    share_failed: "Couldn't create a share link. Please try again.",
    why_title: "Why advertise on CritterPosts",
    why_sub: "A focused audience, honest pricing, and the data to prove it's working.",
    pricing_title: "Simple, honest pricing",
    pricing_sub: "Flat daily rate per placement — no auctions, no hidden fees. Longer runs save more.",
    see_where: "See where it appears",
    tier: (d, m) => <>{d}% off {m}+ days</>,
    final_title: "Ready to get your brand seen?",
    final_sub: "Pick a placement, upload your banner, and go live the moment we confirm your e-Transfer.",
    final_cta_eligible: "Go to booking form",
    final_cta_business: "Become a verified business",
    final_cta_guest: "Get started",
    verified_note: "Banner ads are available to verified business accounts",
    premium_perks_title: "Why go Premium",
    premium_perks: [
      "Very top of the homepage — the first thing every visitor sees",
      "Above the fold — no scrolling needed to be noticed",
      "Exclusive solo slot — not shared in bundle discounts",
      "Maximum brand visibility for your business",
    ],
    scarcity: "Limited — only 1 premium slot available",
    design: {
      badge: "Need a hand with design?",
      title: "No design team? We've got you.",
      body: "Don't have a banner ready — or a designer to make one? Our in-house design team can create a polished, on-brand banner for any placement. Just tell us what you need and we'll handle the artwork.",
      cta: "Message our design team",
      note: "A free helping hand for advertisers — reach out and we'll take it from there.",
      subject: "Banner design services",
    },
    design_gallery: {
      title: "Banners our design team has made",
      sub: "A few samples designed for CritterPosts advertisers — this is the kind of quality you can expect.",
      captions: ["Pet food & nutrition brand", "Grooming salon promotion", "Adoption shelter campaign"],
    },
    boost: {
      title: "Featured vs. Pinned — what's the difference?",
      sub: "Two ways the CritterPosts team can give a listing extra visibility.",
      featured_name: "Featured",
      featured_body: "Appears in the Featured carousel at the top of the Home and Browse pages, marked with a star badge — a rotating showcase of standout listings.",
      pinned_name: "Pinned",
      pinned_body: "Sticks to the very top of the listing results grid with a pin badge, so it's the first thing shoppers see as they scroll — independent of the featured carousel.",
      note: "Both are editorial boosts chosen by our team — they aren't purchased like the banner ad placements above.",
    },
    status: { available: "Available now", filling: "Booking fast", busy: "Nearly full" },
    placements: {
      home_top: { name: "Home top banner", tagline: "Premium — the very first thing visitors see", where: "A premium leaderboard banner at the very top of the CritterPosts.ca homepage, above the featured listings — the first thing every visitor sees.", best: "Launches & flagship campaigns — the most prominent, above-the-fold spot on the site." },
      home: { name: "Home leaderboard", tagline: "Seen by everyone browsing the homepage", where: "A wide leaderboard banner on the CritterPosts.ca homepage, just below the featured listings — in front of everyone scrolling the front page.", best: "Brand awareness & big launches — a prominent banner on the busiest page of the site." },
      browse: { name: "Browse strip", tagline: "In front of active shoppers", where: "A slim banner across the top of the Browse results page, right above the grid — seen while people search and filter pets, supplies and services.", best: "Promotions & offers — catch buyers at the exact moment they're shopping." },
      browse_bottom: { name: "Browse bottom strip", tagline: "Catches shoppers who scrolled it all", where: "A strip banner at the very bottom of the Browse results page, below the full grid of listings — the last thing shoppers see after browsing every result.", best: "Retargeting & next-step offers — reach engaged shoppers who viewed the whole page." },
      browse_sidebar: { name: "Browse sidebar box", tagline: "Always in view while they browse", where: "A tall rectangle box inside the Browse filters sidebar — stays beside the results as shoppers scroll through listings.", best: "Sustained visibility — a persistent presence next to every search result." },
      listing: { name: "Listing rectangle", tagline: "High-intent, ready-to-decide viewers", where: "A rectangle box in the sidebar of every listing detail page, right beside the seller contact card — next to people making a decision.", best: "Conversions — reach buyers who are seconds away from contacting a seller." },
    },
    benefits: [
      { title: "Engaged pet audience", body: "Thousands of Canadians browse CritterPosts every week for pets, supplies and services — a focused, high-intent audience." },
      { title: "Premium, visible slots", body: "Six hand-picked positions across the busiest pages, each shown in a clean, uncluttered layout." },
      { title: "Real performance data", body: "Track live impressions, clicks and CTR for every campaign and download reports whenever you like." },
      { title: "Trusted environment", body: "Your brand runs alongside verified sellers on a modern, moderated marketplace — never spam." },
    ],
  },
  fr: {
    hero_badge: "Où votre annonce apparaît",
    hero_title_1: "Voyez exactement où",
    hero_title_2: "votre bannière s'affiche",
    hero_sub: "CritterPosts.ca compte six emplacements publicitaires premium sur ses pages les plus fréquentées. Voici une visite visuelle de chacun — et pourquoi y placer votre marque la met devant les amoureux des animaux au Canada.",
    cta_start: "Commencer à annoncer",
    cta_tour: "Découvrir les emplacements",
    from_price: (c, r) => <>À partir de <strong className="text-white">{c} {r}$/jour</strong> · remises automatiques sur les longues durées · payez par virement Interac</>,
    stats: [
      { value: "6", label: "emplacements premium" },
      { value: "100%", label: "options de visibilité" },
      { value: "CAD", label: "tarif journalier fixe" },
      { value: "Interac", label: "paiement simple" },
    ],
    tour_title: "Une visite visuelle de chaque emplacement",
    tour_sub: "Chaque zone surlignée ci-dessous montre l'endroit exact qu'occupe votre bannière sur le site réel.",
    try_hint: "Envie de visualiser ? Basculez n'importe quel emplacement sur « Aperçu de mon annonce » et déposez votre propre bannière.",
    where_label: "Où elle apparaît",
    preview_label: "Aperçu de mon annonce",
    where_it_shows: "Où elle s'affiche :",
    best_for: "Idéal pour :",
    per_day: "/jour",
    ideal_image: "Image idéale :",
    book_slot: "Réserver cet emplacement",
    book_banner: "Réserver cette bannière",
    compare_all: "Comparez votre bannière dans chaque emplacement",
    gallery_title: "Votre bannière dans chaque emplacement",
    gallery_sub: "Voyez votre visuel dans chaque emplacement d'un coup d'œil, puis réservez celui que vous voulez — ou groupez-en plusieurs et économisez.",
    gallery_select: "Ajouter au forfait",
    gallery_selected: "Dans le forfait",
    book_n: (n) => `Réserver ${n} emplacements`,
    bundle_save: (pct) => `${pct}% de rabais forfait`,
    bundle_per_day: (cur, amt) => `${cur} ${amt}$/jour`,
    upload_cta: "Glissez-déposez votre bannière, ou cliquez pour téléverser",
    drop_here: "Déposez votre bannière pour l'aperçu",
    uploading: "Téléversement…",
    uploaded_note: "Voici votre bannière dans cet emplacement. Téléversez une autre image quand vous voulez.",
    share_btn: "Partager cet aperçu",
    sharing: "Préparation du lien…",
    share_copied: "Lien de l'aperçu copié — envoyez-le à un collègue !",
    share_failed: "Impossible de créer le lien. Veuillez réessayer.",
    why_title: "Pourquoi annoncer sur CritterPosts",
    why_sub: "Une audience ciblée, des prix honnêtes et les données pour le prouver.",
    pricing_title: "Des prix simples et honnêtes",
    pricing_sub: "Tarif journalier fixe par emplacement — pas d'enchères, pas de frais cachés. Les longues durées coûtent moins cher.",
    see_where: "Voir où elle apparaît",
    tier: (d, m) => <>{d}% de rabais dès {m} jours</>,
    final_title: "Prêt à faire voir votre marque ?",
    final_sub: "Choisissez un emplacement, téléversez votre bannière, et elle est en ligne dès que nous confirmons votre virement Interac.",
    final_cta_eligible: "Aller au formulaire de réservation",
    final_cta_business: "Devenir une entreprise vérifiée",
    final_cta_guest: "Commencer",
    verified_note: "Les bannières sont réservées aux comptes entreprise vérifiés",
    premium_perks_title: "Pourquoi choisir Premium",
    premium_perks: [
      "Tout en haut de la page d'accueil — la première chose que voient les visiteurs",
      "Au-dessus de la ligne de flottaison — visible sans défilement",
      "Emplacement exclusif et unique — hors des rabais de forfait",
      "Visibilité maximale pour votre marque",
    ],
    scarcity: "Limité — un seul emplacement premium disponible",
    design: {
      badge: "Besoin d'aide pour le design ?",
      title: "Pas d'équipe de design ? On s'en occupe.",
      body: "Vous n'avez pas de bannière prête — ni de designer pour la créer ? Notre équipe de design peut concevoir une bannière soignée et à votre image pour tout emplacement. Dites-nous ce qu'il vous faut et nous nous occupons du visuel.",
      cta: "Écrire à notre équipe de design",
      note: "Un coup de main gratuit pour les annonceurs — contactez-nous et on s'occupe du reste.",
      subject: "Services de conception de bannière",
    },
    design_gallery: {
      title: "Des bannières conçues par notre équipe",
      sub: "Quelques exemples réalisés pour des annonceurs de CritterPosts — voici le niveau de qualité auquel vous pouvez vous attendre.",
      captions: ["Marque de nourriture pour animaux", "Promotion de salon de toilettage", "Campagne de refuge d'adoption"],
    },
    boost: {
      title: "En vedette ou épinglé — quelle différence ?",
      sub: "Deux façons pour l'équipe CritterPosts d'offrir plus de visibilité à une annonce.",
      featured_name: "En vedette",
      featured_body: "Apparaît dans le carrousel « En vedette » en haut des pages Accueil et Recherche, avec un badge étoile — une vitrine rotative des meilleures annonces.",
      pinned_name: "Épinglé",
      pinned_body: "Reste tout en haut de la grille de résultats avec un badge épingle, pour être la première chose que voient les acheteurs — indépendamment du carrousel en vedette.",
      note: "Ce sont deux mises en avant éditoriales choisies par notre équipe — elles ne s'achètent pas comme les bannières ci-dessus.",
    },
    status: { available: "Disponible maintenant", filling: "Se remplit vite", busy: "Presque complet" },
    placements: {
      home_top: { name: "Bandeau haut accueil", tagline: "Premium — la toute première chose que voient les visiteurs", where: "Une bannière bandeau premium tout en haut de la page d'accueil de CritterPosts.ca, au-dessus des annonces en vedette — la première chose que voit chaque visiteur.", best: "Lancements et campagnes phares — l'emplacement le plus en vue, au-dessus de la ligne de flottaison." },
      home: { name: "Bandeau accueil", tagline: "Vu par tous ceux qui parcourent l'accueil", where: "Une large bannière sur la page d'accueil de CritterPosts.ca, juste sous les annonces en vedette — devant tous ceux qui parcourent la page d'accueil.", best: "Notoriété de marque et grands lancements — une bannière bien en vue sur la page la plus fréquentée du site." },
      browse: { name: "Bandeau recherche", tagline: "Devant les acheteurs actifs", where: "Une bannière fine en haut de la page de résultats, juste au-dessus de la grille — vue pendant que les gens cherchent et filtrent animaux, articles et services.", best: "Promotions et offres — captez les acheteurs au moment précis où ils magasinent." },
      browse_bottom: { name: "Bandeau bas recherche", tagline: "Capte ceux qui ont tout parcouru", where: "Une bannière horizontale tout en bas de la page de résultats, sous la grille complète d'annonces — la dernière chose que voient les acheteurs après avoir tout parcouru.", best: "Reciblage et offres de suivi — touchez les acheteurs engagés qui ont vu toute la page." },
      browse_sidebar: { name: "Encadré latéral recherche", tagline: "Toujours visible pendant la navigation", where: "Un grand encadré vertical dans la barre latérale des filtres de recherche — reste à côté des résultats pendant que les visiteurs défilent.", best: "Visibilité soutenue — une présence constante à côté de chaque résultat de recherche." },
      listing: { name: "Encadré fiche annonce", tagline: "Visiteurs à forte intention, prêts à décider", where: "Un encadré rectangulaire dans la barre latérale de chaque fiche d'annonce, juste à côté de la carte de contact du vendeur — près des gens qui décident.", best: "Conversions — atteignez les acheteurs à quelques secondes de contacter un vendeur." },
    },
    benefits: [
      { title: "Audience passionnée d'animaux", body: "Des milliers de Canadiens parcourent CritterPosts chaque semaine pour des animaux, des articles et des services — une audience ciblée et motivée." },
      { title: "Emplacements premium et visibles", body: "Six positions soigneusement choisies sur les pages les plus fréquentées, présentées dans une mise en page épurée." },
      { title: "Vraies données de performance", body: "Suivez impressions, clics et CTR en direct pour chaque campagne et téléchargez des rapports quand vous voulez." },
      { title: "Environnement de confiance", body: "Votre marque côtoie des vendeurs vérifiés sur une place de marché moderne et modérée — jamais de pourriel." },
    ],
  },
};

const STATUS_CLS = {
  available: "bg-emerald-100 text-emerald-700",
  filling: "bg-amber-100 text-amber-700",
  busy: "bg-red-100 text-red-700",
};

export default function PlacementsInfo() {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const location = useLocation();
  const c = COPY[lang] || COPY.en;
  const [pricing, setPricing] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [myImage, setMyImage] = useState(null);
  const [myPath, setMyPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const [mineOn, setMineOn] = useState({});
  const [lightbox, setLightbox] = useState(null);
  const [deliveredBanners, setDeliveredBanners] = useState([]);
  useEffect(() => {
    api.get("/gallery/banners").then((r) => setDeliveredBanners(r.data.banners || [])).catch(() => {});
  }, []);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const refs = useRef({});
  const eligible = user && (user.is_verified_seller || user.verified_seller || user.verified_business);

  useEffect(() => {
    api.get("/ads/public-pricing").then((r) => setPricing(r.data)).catch(() => {});
    api.get("/ads/availability").then((r) => setAvailability(r.data.placements)).catch(() => {});
  }, []);

  // Load a shared preview (?preview=<path>&slot=<placement>) so colleagues see the same mockup
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const preview = params.get("preview");
    const slot = params.get("slot");
    if (preview) {
      setMyPath(preview);
      setMyImage(photoUrl(preview));
      const key = PLACEMENT_META.some((p) => p.key === slot) ? slot : "home";
      setMineOn((m) => ({ ...m, [key]: true }));
      setTimeout(() => refs.current[key]?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, [location.search]);

  useEffect(() => {
    const hash = location.hash.replace("#placement-", "").replace("#", "");
    if (hash && refs.current[hash]) {
      setTimeout(() => refs.current[hash].scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    }
  }, [location.hash, pricing]);

  const uploadPreviewFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ads/preview-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMyPath(data.path);
      setMyImage(photoUrl(data.path));
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setUploading(false);
    }
  };

  const onUpload = (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    uploadPreviewFile(file);
  };

  const onDrop = (key) => (e) => {
    e.preventDefault();
    setDragKey(null);
    const file = (e.dataTransfer?.files || [])[0];
    if (file) {
      setMineOn((m) => ({ ...m, [key]: true }));
      uploadPreviewFile(file);
    }
  };

  const sharePreview = async (key) => {
    if (!myPath) return;
    setSharing(true);
    try {
      const url = `${window.location.origin}/advertising/placements?preview=${encodeURIComponent(myPath)}&slot=${key}`;
      if (navigator.share) {
        try { await navigator.share({ title: "My ad preview — CritterPosts.ca", url }); setSharing(false); return; }
        catch { /* user cancelled or unsupported — fall through to copy */ }
      }
      await navigator.clipboard.writeText(url);
      toast.success(c.share_copied);
    } catch {
      toast.error(c.share_failed);
    } finally {
      setSharing(false);
    }
  };

  const rateFor = (k) => pricing?.rates?.[k];
  const currency = pricing?.currency || "CAD";
  const minRate = pricing?.rates ? Math.min(...Object.values(pricing.rates)) : null;
  const bookLink = (key) => (myPath ? `/advertise?banner=${encodeURIComponent(myPath)}&placement=${key}` : "/advertise");
  const toggleSelect = (key) => setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));
  const bundlePct = (() => {
    const bd = pricing?.bundle_discounts || {};
    return Number(bd[selected.length] ?? bd[String(selected.length)] ?? 0);
  })();
  const bundlePerDay = selected.reduce((sum, k) => sum + (rateFor(k) || 0), 0);
  const bundlePerDayNet = +(bundlePerDay * (1 - bundlePct / 100)).toFixed(2);
  const bundleBookLink = `/advertise?${myPath ? `banner=${encodeURIComponent(myPath)}&` : ""}slots=${selected.join(",")}`;

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#12234A] text-white">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#F5551E]/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-[#F5551E]/10 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-16 text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#F5551E] bg-white/10 rounded-full px-3 py-1" data-testid="placements-hero-badge"><MapPin size={13} /> {c.hero_badge}</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">{c.hero_title_1}<br className="hidden sm:block" /> {c.hero_title_2}</h1>
          <p className="text-base text-slate-300 mt-5 max-w-2xl mx-auto">{c.hero_sub}</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/advertise"><Button data-testid="placements-cta-hero" className="rounded-xl h-12 px-6 bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">{c.cta_start} <ArrowRight size={17} /></Button></Link>
            <a href="#placement-home"><Button variant="outline" className="rounded-xl h-12 px-6 font-semibold bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">{c.cta_tour}</Button></a>
          </div>
          {minRate != null && <p className="text-xs text-slate-400 mt-5">{c.from_price(currency, minRate)}</p>}
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-[#FFF4F0] border-b border-[#F5551E]/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-7 grid grid-cols-2 sm:grid-cols-4 gap-6">
          {c.stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-display font-extrabold text-2xl sm:text-3xl text-[#F5551E]">{s.value}</p>
              <p className="text-xs text-slate-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Placement tour */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="font-display font-bold text-3xl text-[#12234A]">{c.tour_title}</h2>
          <p className="text-sm text-slate-500 mt-2 max-w-xl mx-auto">{c.tour_sub}</p>
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F5551E] bg-[#FFF4F0] rounded-full px-3 py-1.5 mt-4"><Sparkles size={13} /> {c.try_hint}</p>
          <div className="mt-4">
            <Button type="button" variant="outline" onClick={() => setGalleryOpen(true)} data-testid="placements-gallery-open" className="rounded-full h-10 px-5 gap-2 border-[#12234A]/15 text-[#12234A] font-semibold hover:bg-slate-50">
              <LayoutGrid size={16} className="text-[#F5551E]" /> {c.compare_all}
            </Button>
          </div>
        </div>

        <div className="space-y-16">
          {PLACEMENT_META.map((p, i) => {
            const pc = c.placements[p.key];
            const Icon = p.icon;
            const av = availability?.[p.key];
            const showMine = !!mineOn[p.key];
            return (
              <div
                key={p.key}
                id={`placement-${p.key}`}
                ref={(el) => (refs.current[p.key] = el)}
                className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center scroll-mt-24 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
                data-testid={`placement-section-${p.key}`}
              >
                <div className="relative">
                  {/* view toggle */}
                  <div className="flex items-center gap-1 p-1 mb-3 rounded-xl bg-slate-100 w-fit" data-testid={`placement-toggle-${p.key}`}>
                    <button type="button" onClick={() => setMineOn((m) => ({ ...m, [p.key]: false }))} data-testid={`placement-toggle-where-${p.key}`} className={`text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-colors ${!showMine ? "bg-white text-[#12234A] shadow-sm" : "text-slate-500"}`}>{c.where_label}</button>
                    <button type="button" onClick={() => setMineOn((m) => ({ ...m, [p.key]: true }))} data-testid={`placement-toggle-mine-${p.key}`} className={`text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-colors ${showMine ? "bg-white text-[#F5551E] shadow-sm" : "text-slate-500"}`}>{c.preview_label}</button>
                  </div>

                  {showMine ? (
                    <div
                      data-testid={`placement-mockup-${p.key}`}
                      onDragOver={(e) => { e.preventDefault(); setDragKey(p.key); }}
                      onDragLeave={() => setDragKey((k) => (k === p.key ? null : k))}
                      onDrop={onDrop(p.key)}
                      className={`rounded-xl transition-all ${dragKey === p.key ? "ring-2 ring-[#F5551E] ring-offset-2" : ""}`}
                    >
                      <div className="relative">
                        <BannerMockup placement={p.key} image={myImage} />
                        {dragKey === p.key && (
                          <div className="absolute inset-0 rounded-xl bg-[#FFF4F0]/90 border-2 border-dashed border-[#F5551E] flex flex-col items-center justify-center gap-1 pointer-events-none" data-testid={`placement-drop-overlay-${p.key}`}>
                            <ImagePlus size={22} className="text-[#F5551E]" />
                            <span className="text-xs font-bold text-[#F5551E]">{c.drop_here}</span>
                          </div>
                        )}
                        {uploading && (
                          <div className="absolute inset-0 rounded-xl bg-white/70 flex items-center justify-center" data-testid={`placement-uploading-${p.key}`}>
                            <Loader2 size={24} className="animate-spin text-[#F5551E]" />
                          </div>
                        )}
                      </div>
                      {myImage ? (
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                          <p className="text-[11px] text-emerald-700 flex items-center gap-1"><Check size={12} /> {c.uploaded_note}</p>
                          <div className="flex items-center gap-2">
                            <Button type="button" size="sm" variant="outline" onClick={() => sharePreview(p.key)} disabled={sharing || !myPath} data-testid={`placement-share-${p.key}`} className="rounded-full h-8 text-xs gap-1.5 border-[#F5551E]/30 text-[#F5551E] hover:bg-[#FFF4F0] hover:text-[#E04812]">
                              {sharing ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />} {sharing ? c.sharing : c.share_btn}
                            </Button>
                            <Link to={bookLink(p.key)}><Button type="button" size="sm" data-testid={`placement-book-banner-${p.key}`} className="rounded-full h-8 text-xs gap-1.5 bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold">{c.book_banner} <ArrowRight size={13} /></Button></Link>
                          </div>
                        </div>
                      ) : (
                        <label className="mt-2 flex items-center justify-center gap-1.5 h-11 rounded-xl border-2 border-dashed border-slate-300 cursor-pointer hover:border-[#F5551E] hover:bg-[#FFF9F6] transition-colors" data-testid={`placement-upload-${p.key}`}>
                          {uploading ? <Loader2 size={15} className="animate-spin text-[#F5551E]" /> : <ImagePlus size={15} className="text-[#F5551E]" />}
                          <span className="text-xs font-semibold text-slate-500">{uploading ? c.uploading : c.upload_cta}</span>
                          <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
                        </label>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg bg-slate-50">
                      <img src={p.img} alt={`${pc.name} — ${c.where_label}`} className="w-full h-auto block" data-testid={`placement-image-${p.key}`} />
                    </div>
                  )}
                  <span className="absolute -top-3 -right-3 w-12 h-12 rounded-2xl bg-[#F5551E] text-white font-display font-extrabold text-xl flex items-center justify-center shadow-lg z-10">{i + 1}</span>
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 text-[#F5551E]">
                      <Icon size={20} />
                      <span className="text-xs font-bold uppercase tracking-widest">{pc.tagline}</span>
                    </div>
                    {av && (
                      <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full ${STATUS_CLS[av.status]}`} data-testid={`placement-status-${p.key}`}>
                        <CircleDot size={11} /> {c.status[av.status]}
                      </span>
                    )}
                    {p.premium && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700" data-testid={`placement-scarcity-${p.key}`}>
                        <Sparkles size={11} /> {c.scarcity}
                      </span>
                    )}
                  </div>
                  <h3 className="font-display font-extrabold text-2xl sm:text-3xl text-[#12234A] mt-2">{pc.name}</h3>
                  <div className="mt-4 space-y-3">
                    <div className="flex items-start gap-2.5">
                      <MapPin size={17} className="text-[#F5551E] mt-0.5 shrink-0" />
                      <p className="text-sm text-slate-600"><span className="font-semibold text-[#12234A]">{c.where_it_shows} </span>{pc.where}</p>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <Sparkles size={17} className="text-[#F5551E] mt-0.5 shrink-0" />
                      <p className="text-sm text-slate-600"><span className="font-semibold text-[#12234A]">{c.best_for} </span>{pc.best}</p>
                    </div>
                  </div>
                  {p.premium && (
                    <div className="mt-4 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4" data-testid={`placement-perks-${p.key}`}>
                      <p className="text-xs font-bold text-amber-700 uppercase tracking-wide flex items-center gap-1 mb-2"><Sparkles size={13} /> {c.premium_perks_title}</p>
                      <ul className="space-y-1.5">
                        {c.premium_perks.map((it, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-600"><Check size={14} className="text-amber-500 mt-0.5 shrink-0" /> {it}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-3 mt-5">
                    {rateFor(p.key) != null && (
                      <span className="inline-flex items-baseline gap-1 rounded-xl bg-[#FFF4F0] border border-[#F5551E]/20 px-3 py-2" data-testid={`placement-price-${p.key}`}>
                        <span className="font-display font-extrabold text-lg text-[#F5551E]">{currency} ${rateFor(p.key)}</span>
                        <span className="text-xs text-slate-500">{c.per_day}</span>
                      </span>
                    )}
                    <span className="text-xs text-slate-400">{c.ideal_image} <strong className="text-[#12234A]">{p.size}</strong></span>
                  </div>
                  <Link to={bookLink(p.key)}>
                    <Button variant="ghost" data-testid={`placement-book-${p.key}`} className="mt-4 px-0 text-[#F5551E] font-semibold gap-1 hover:bg-transparent hover:gap-2 transition-all">{c.book_slot} <ArrowRight size={15} /></Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Why advertise */}
      <section className="bg-[#F8FAFC] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
          <div className="text-center mb-10">
            <h2 className="font-display font-bold text-3xl text-[#12234A]">{c.why_title}</h2>
            <p className="text-sm text-slate-500 mt-2 max-w-xl mx-auto">{c.why_sub}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {c.benefits.map((b, i) => {
              const BIcon = BENEFIT_ICONS[i];
              return (
                <div key={b.title} className="bg-white rounded-2xl border border-slate-200/80 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`placements-benefit-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-[#FFF4F0] flex items-center justify-center"><BIcon size={20} className="text-[#F5551E]" /></div>
                  <h3 className="font-display font-bold text-[#12234A] mt-3">{b.title}</h3>
                  <p className="text-sm text-slate-500 mt-1.5">{b.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="font-display font-bold text-3xl text-[#12234A]">{c.pricing_title}</h2>
          <p className="text-sm text-slate-500 mt-2">{c.pricing_sub}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLACEMENT_META.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.key} className="rounded-2xl border border-slate-200/80 p-6 text-center hover:border-[#F5551E]/40 hover:shadow-md transition-all" data-testid={`placements-pricing-${p.key}`}>
                <div className="w-12 h-12 rounded-xl bg-[#12234A] flex items-center justify-center mx-auto"><Icon size={22} className="text-[#F5551E]" /></div>
                <h3 className="font-display font-bold text-[#12234A] mt-4">{c.placements[p.key].name}</h3>
                {rateFor(p.key) != null ? (
                  <p className="font-display font-extrabold text-3xl text-[#F5551E] mt-3">${rateFor(p.key)}<span className="text-sm text-slate-400 font-normal">{c.per_day}</span></p>
                ) : <p className="font-display font-extrabold text-2xl text-slate-300 mt-3">—</p>}
                <a href={`#placement-${p.key}`}><Button variant="ghost" className="mt-3 text-xs text-slate-500 hover:text-[#F5551E]">{c.see_where}</Button></a>
              </div>
            );
          })}
        </div>
        {pricing?.tiers?.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-4 mt-8 text-sm">
            {pricing.tiers.filter((t) => t.discount > 0).sort((a, b) => a.min_days - b.min_days).map((t) => (
              <span key={t.min_days} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 font-semibold" data-testid={`placements-tier-${t.min_days}`}>
                <Check size={14} /> {c.tier(t.discount, t.min_days)}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Banner design services */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-4 pt-4" data-testid="design-services">
        <div className="relative overflow-hidden rounded-3xl bg-[#12234A] text-white p-7 sm:p-10">
          <div className="absolute -top-16 -right-10 w-72 h-72 rounded-full bg-[#F5551E]/20 blur-3xl" />
          <div className="relative flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="w-14 h-14 rounded-2xl bg-[#F5551E] flex items-center justify-center shrink-0"><ImagePlus size={26} className="text-white" /></div>
            <div className="flex-1 min-w-0">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#F5551E] bg-white/10 rounded-full px-3 py-1"><Sparkles size={13} /> {c.design.badge}</span>
              <h2 className="font-display font-extrabold text-2xl sm:text-3xl mt-3">{c.design.title}</h2>
              <p className="text-sm text-slate-300 mt-2 max-w-2xl">{c.design.body}</p>
              <p className="text-xs text-slate-400 mt-2">{c.design.note}</p>
            </div>
            <Link to={`/contact?subject=${encodeURIComponent(c.design.subject)}#contact-form-section`} className="shrink-0" data-testid="design-services-cta">
              <Button className="rounded-xl h-12 px-6 bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">{c.design.cta} <ArrowRight size={17} /></Button>
            </Link>
          </div>

          {/* Sample banners the design team has made */}
          <div className="relative mt-8 pt-7 border-t border-white/10" data-testid="design-gallery">
            <p className="font-display font-bold text-lg text-white">{c.design_gallery.title}</p>
            <p className="text-xs text-slate-400 mt-1 max-w-2xl">{c.design_gallery.sub}</p>
            <div className="grid sm:grid-cols-3 gap-4 mt-5">
              {SAMPLE_BANNERS.map((src, i) => (
                <figure key={i} className="group" data-testid={`design-sample-${i}`}>
                  <button type="button" onClick={() => setLightbox({ src, caption: c.design_gallery.captions[i] })} data-testid={`design-sample-open-${i}`} className="block w-full rounded-xl overflow-hidden border border-white/15 bg-white/5 shadow-lg cursor-zoom-in">
                    <img src={src} alt={c.design_gallery.captions[i]} loading="lazy" className="w-full h-auto block transition-transform duration-300 group-hover:scale-[1.03]" />
                  </button>
                  <figcaption className="text-[11px] text-slate-400 mt-2 flex items-center gap-1.5"><Sparkles size={11} className="text-[#F5551E]" /> {c.design_gallery.captions[i]}</figcaption>
                </figure>
              ))}
              {deliveredBanners.map((b, i) => {
                const src = photoUrl(b.image);
                const caption = b.placement || "Custom banner";
                return (
                  <figure key={`dl-${i}`} className="group" data-testid={`design-delivered-${i}`}>
                    <button type="button" onClick={() => setLightbox({ src, caption })} data-testid={`design-delivered-open-${i}`} className="block w-full rounded-xl overflow-hidden border border-white/15 bg-white/5 shadow-lg cursor-zoom-in">
                      <img src={src} alt={caption} loading="lazy" className="w-full h-auto block transition-transform duration-300 group-hover:scale-[1.03]" />
                    </button>
                    <figcaption className="text-[11px] text-slate-400 mt-2 flex items-center gap-1.5"><Sparkles size={11} className="text-[#F5551E]" /> {caption}</figcaption>
                  </figure>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Featured vs Pinned explainer */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-4" data-testid="boost-explainer">
        <div className="rounded-3xl border border-slate-200/80 bg-slate-50/60 p-6 sm:p-8">
          <div className="text-center mb-7">
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#12234A]">{c.boost.title}</h2>
            <p className="text-sm text-slate-500 mt-2 max-w-xl mx-auto">{c.boost.sub}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-[#F5551E]/20 p-5" data-testid="boost-featured">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F5551E] text-white text-xs font-bold px-2.5 py-1"><Star size={12} className="fill-white" /> {c.boost.featured_name}</span>
              <p className="text-sm text-slate-600 mt-3">{c.boost.featured_body}</p>
            </div>
            <div className="bg-white rounded-2xl border border-[#12234A]/15 p-5" data-testid="boost-pinned">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#12234A] text-white text-xs font-semibold px-2.5 py-1"><Pin size={12} className="fill-white" /> {c.boost.pinned_name}</span>
              <p className="text-sm text-slate-600 mt-3">{c.boost.pinned_body}</p>
            </div>
          </div>
          <p className="text-xs text-slate-400 text-center mt-5 max-w-2xl mx-auto">{c.boost.note}</p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#12234A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#F5551E] flex items-center justify-center mx-auto mb-5"><Megaphone size={26} className="text-white" /></div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl">{c.final_title}</h2>
          <p className="text-slate-300 mt-3 max-w-lg mx-auto">{c.final_sub}</p>
          <div className="mt-8">
            {eligible ? (
              <Link to="/advertise"><Button data-testid="placements-cta-final" className="rounded-xl h-12 px-8 bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">{c.final_cta_eligible} <ArrowRight size={17} /></Button></Link>
            ) : (
              <div className="inline-flex flex-col items-center gap-2">
                <span className="inline-flex items-center gap-2 text-sm text-slate-300"><BadgeCheck size={16} className="text-[#F5551E]" /> {c.verified_note}</span>
                <Link to={user ? "/dashboard" : "/advertise"}><Button data-testid="placements-cta-final" className="rounded-xl h-12 px-8 bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">{user ? c.final_cta_business : c.final_cta_guest} <ArrowRight size={17} /></Button></Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Preview gallery — all four slots at once */}
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="placements-gallery-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LayoutGrid size={18} className="text-[#F5551E]" /> {c.gallery_title}</DialogTitle>
            <DialogDescription>{c.gallery_sub}</DialogDescription>
          </DialogHeader>
          {!myImage && (
            <label className="flex items-center justify-center gap-1.5 h-11 rounded-xl border-2 border-dashed border-slate-300 cursor-pointer hover:border-[#F5551E] hover:bg-[#FFF9F6] transition-colors" data-testid="placements-gallery-upload">
              {uploading ? <Loader2 size={15} className="animate-spin text-[#F5551E]" /> : <ImagePlus size={15} className="text-[#F5551E]" />}
              <span className="text-xs font-semibold text-slate-500">{uploading ? c.uploading : c.upload_cta}</span>
              <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
            </label>
          )}
          <div className="grid sm:grid-cols-2 gap-5 mt-1">
            {PLACEMENT_META.map((p) => {
              const Icon = p.icon;
              const isSel = selected.includes(p.key);
              return (
                <div key={p.key} className={`rounded-2xl border p-4 transition-colors ${isSel ? "border-[#F5551E] bg-[#FFF9F6]" : "border-slate-200/80"}`} data-testid={`gallery-slot-${p.key}`}>
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="inline-flex items-center gap-1.5 text-sm font-display font-bold text-[#12234A]"><Icon size={16} className="text-[#F5551E]" /> {c.placements[p.key].name}{p.premium && <span className="text-[9px] font-bold uppercase tracking-wide bg-[#12234A] text-white rounded-full px-1.5 py-0.5">Premium</span>}</span>
                    {rateFor(p.key) != null && <span className="text-xs font-bold text-[#F5551E]">{currency} ${rateFor(p.key)}{c.per_day}</span>}
                  </div>
                  <BannerMockup placement={p.key} image={myImage} />
                  <div className="flex items-center gap-2 mt-3">
                    {!p.premium && (
                      <button type="button" onClick={() => toggleSelect(p.key)} data-testid={`gallery-select-${p.key}`} aria-pressed={isSel} className={`flex items-center gap-1.5 h-9 px-3 rounded-xl border text-xs font-semibold transition-colors ${isSel ? "border-[#F5551E] bg-[#F5551E] text-white" : "border-slate-300 text-slate-600 hover:border-[#F5551E] hover:text-[#F5551E]"}`}>
                        {isSel ? <Check size={14} /> : <Plus size={14} />} {isSel ? c.gallery_selected : c.gallery_select}
                      </button>
                    )}
                    <Link to={bookLink(p.key)} onClick={() => setGalleryOpen(false)} className="flex-1">
                      <Button size="sm" data-testid={`gallery-book-${p.key}`} className="w-full rounded-xl bg-[#12234A] hover:bg-[#0c182f] text-white font-semibold gap-1.5">{c.book_banner} <ArrowRight size={14} /></Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {selected.length >= 2 && (
            <div className="sticky bottom-0 -mx-6 px-6 pt-3 pb-1 bg-gradient-to-t from-white via-white to-white/70" data-testid="gallery-bundle-bar">
              <div className="rounded-2xl bg-[#12234A] text-white p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-display font-bold flex items-center gap-2"><Layers size={16} className="text-[#F5551E]" /> {selected.map((k) => c.placements[k].name).join(" · ")}</p>
                  <p className="text-xs text-slate-300 mt-0.5">
                    <span className="inline-flex items-center gap-1 font-bold text-emerald-300"><Check size={12} /> {c.bundle_save(bundlePct)}</span>
                    <span className="mx-2 text-slate-500">·</span>
                    <span className="line-through text-slate-400 mr-1.5">{currency} ${bundlePerDay.toFixed(2)}</span>
                    <span className="font-bold text-white">{c.bundle_per_day(currency, bundlePerDayNet.toFixed(2))}</span>
                  </p>
                </div>
                <Link to={bundleBookLink} onClick={() => setGalleryOpen(false)}>
                  <Button data-testid="gallery-book-bundle" className="rounded-xl h-11 px-6 bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">{c.book_n(selected.length)} <ArrowRight size={16} /></Button>
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl bg-[#12234A] border-white/10 p-3 sm:p-4" data-testid="design-lightbox">
          <DialogHeader className="sr-only"><DialogTitle>{lightbox?.caption || "Sample banner"}</DialogTitle><DialogDescription>Full-size sample banner preview</DialogDescription></DialogHeader>
          {lightbox && (
            <div>
              <img src={lightbox.src} alt={lightbox.caption} className="w-full h-auto rounded-xl" />
              <p className="text-xs text-slate-300 mt-3 flex items-center gap-1.5"><Sparkles size={12} className="text-[#F5551E]" /> {lightbox.caption}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
